import {
	FC,
	useEffect,
	useState,
	useCallback,
	useRef,
	DragEvent
} from 'react'
import { useTranslation } from 'react-i18next'
import {
	Flex,
	Spin,
	Button,
	Modal,
	Form,
	Input,
	DatePicker,
	Popconfirm,
	message,
	Typography,
	Collapse,
	Image,
	Tooltip,
	Empty,
	Progress,
	Tag
} from 'antd'
import {
	PlusOutlined,
	DeleteOutlined,
	CameraOutlined,
	ExpandOutlined
} from '@ant-design/icons'
import {
	Inspection,
	InspectionPhoto
} from '../helpers/types/inventory'
import {
	fetchInspections,
	createInspection,
	deleteInspection,
	uploadInspectionPhoto,
	deleteInspectionPhoto
} from '../api/inventory'
const { Text, Title } = Typography

// ─── Helpers ──────────────────────────────────────────────────────────────────
function photoUrl(filePath: string): string {
	// filePath example: "public/inspection/1713430000000-ID_PLATE.jpg"
	// Served at: /inspection/<filename> — proxied in dev, same-origin in prod
	const filename = filePath.split('/').pop() ?? filePath
	return `/inspection/${filename}`
}

// ─── Photo slot component ─────────────────────────────────────────────────────
interface PhotoSlotProps {
	label: string
	photo: InspectionPhoto | undefined
	inspectionId: number
	onUploaded: (photo: InspectionPhoto) => void
	onDeleted: (id: number) => void
}

const PhotoSlot: FC<PhotoSlotProps> = ({
	label,
	photo,
	inspectionId,
	onUploaded,
	onDeleted
}) => {
	const { t } = useTranslation()
	const fileRef = useRef<HTMLInputElement>(null)
	const [uploading, setUploading] = useState(false)
	const [progress, setProgress] = useState(0)
	const [dragging, setDragging] = useState(false)

	const uploadFile = async (file: File) => {
		setUploading(true)
		setProgress(10)
		try {
			const result = await uploadInspectionPhoto(
				inspectionId,
				file,
				label,
				new Date().toISOString()
			)
			setProgress(100)
			onUploaded(result)
		} catch {
			message.error(t('Error'))
		} finally {
			setUploading(false)
			setProgress(0)
		}
	}

	const handleFileChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = e.target.files?.[0]
		if (file) uploadFile(file)
		e.target.value = ''
	}

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setDragging(false)
		const file = e.dataTransfer.files?.[0]
		if (file && file.type.startsWith('image/')) uploadFile(file)
	}

	const handleDelete = async () => {
		if (!photo) return
		try {
			await deleteInspectionPhoto(inspectionId, photo.id)
			onDeleted(photo.id)
		} catch {
			message.error(t('Error'))
		}
	}

	const slotStyle: React.CSSProperties = {
		width: 140,
		height: 120,
		borderRadius: 10,
		border: dragging
			? '2px dashed #3894EB'
			: '1.5px dashed #c5d0de',
		backgroundColor: dragging ? '#EFF6FF' : '#FAFBFC',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		cursor: 'pointer',
		overflow: 'hidden',
		transition: 'border-color .2s'
	}

	return (
		<Flex vertical align="center" gap={4}>
			<div
				style={slotStyle}
				onDragOver={(e) => {
					e.preventDefault()
					setDragging(true)
				}}
				onDragLeave={() => setDragging(false)}
				onDrop={handleDrop}
				onClick={() =>
					!photo && !uploading && fileRef.current?.click()
				}>
				{photo ? (
					<>
						<Image
							src={photoUrl(photo.filePath)}
							alt={label}
							wrapperStyle={{
								width: '100%',
								height: '100%',
								display: 'block'
							}}
							style={{
								width: '100%',
								height: '100%',
								objectFit: 'cover',
								display: 'block'
							}}
							preview={{
								mask: <ExpandOutlined />
							}}
						/>
						<Popconfirm
							title="Delete this photo?"
							onConfirm={handleDelete}
							okText="Delete"
							okButtonProps={{ danger: true }}>
							<Button
								icon={<DeleteOutlined />}
								size="small"
								danger
								style={{
									position: 'absolute',
									top: 4,
									right: 4,
									padding: '0 6px',
									height: 22,
									fontSize: 11
								}}
								onClick={(e) => e.stopPropagation()}
							/>
						</Popconfirm>
					</>
				) : uploading ? (
					<Progress
						type="circle"
						percent={progress}
						size={48}
						strokeColor="#3894EB"
					/>
				) : (
					<Flex
						vertical
						align="center"
						gap={4}
						style={{ pointerEvents: 'none' }}>
						<CameraOutlined
							style={{
								fontSize: 24,
								color: '#9BB3CC'
							}}
						/>
						<Text
							style={{ fontSize: 10, color: '#9BB3CC' }}>
							Upload
						</Text>
					</Flex>
				)}
			</div>
			<Text
				style={{
					fontSize: 10,
					color: '#6B7A8D',
					textAlign: 'center',
					maxWidth: 140,
					wordBreak: 'break-word'
				}}>
				{label}
			</Text>
			<input
				ref={fileRef}
				type="file"
				accept="image/*"
				capture="environment"
				style={{ display: 'none' }}
				onChange={handleFileChange}
			/>
		</Flex>
	)
}

// ─── Known photo slots for structured inspections ─────────────────────────────
const KNOWN_SLOTS = [
	'ID_PLATE',
	'OVERVIEW_FRONT',
	'OVERVIEW_REAR',
	'HVAC_COMPRESSOR',
	'HVAC_EVAPORATOR',
	'HEATING_UNIT',
	'FIRE_PANEL',
	'CONTROL_BOARD'
]

// ─── Photos gallery in an inspection ─────────────────────────────────────────
interface InspectionPhotosProps {
	inspection: Inspection
	onPhotoUploaded: (
		inspectionId: number,
		photo: InspectionPhoto
	) => void
	onPhotoDeleted: (inspectionId: number, photoId: number) => void
}

const InspectionPhotos: FC<InspectionPhotosProps> = ({
	inspection,
	onPhotoUploaded,
	onPhotoDeleted
}) => {
	const [customSlot, setCustomSlot] = useState('')

	const photoMap = new Map(
		inspection.photos.map((p) => [p.photoId, p])
	)

	// Build slot list: known + any extras already uploaded + user-added custom
	const extraSlots = inspection.photos
		.filter((p) => !KNOWN_SLOTS.includes(p.photoId))
		.map((p) => p.photoId)
	const uniqueExtra = Array.from(new Set(extraSlots))
	const allSlots = [...KNOWN_SLOTS, ...uniqueExtra]

	return (
		<Flex vertical gap={12} style={{ padding: '8px 0' }}>
			<Flex wrap="wrap" gap={12}>
				{allSlots.map((slot) => (
					<PhotoSlot
						key={slot}
						label={slot}
						photo={photoMap.get(slot)}
						inspectionId={inspection.id}
						onUploaded={(p) =>
							onPhotoUploaded(inspection.id, p)
						}
						onDeleted={(id) =>
							onPhotoDeleted(inspection.id, id)
						}
					/>
				))}
			</Flex>

			{/* Custom slot upload */}
			<Flex gap={8} align="center">
				<Input
					placeholder="Custom slot name, e.g. DOOR_SEAL"
					value={customSlot}
					onChange={(e) =>
						setCustomSlot(
							e.target.value
								.toUpperCase()
								.replace(/\s+/g, '_')
						)
					}
					style={{ maxWidth: 240 }}
					size="small"
				/>
				{customSlot && (
					<PhotoSlot
						label={customSlot}
						photo={undefined}
						inspectionId={inspection.id}
						onUploaded={(p) => {
							onPhotoUploaded(inspection.id, p)
							setCustomSlot('')
						}}
						onDeleted={(id) =>
							onPhotoDeleted(inspection.id, id)
						}
					/>
				)}
			</Flex>
		</Flex>
	)
}

// ─── Add Inspection modal ─────────────────────────────────────────────────────
interface AddInspectionModalProps {
	carrierId: number
	open: boolean
	onClose: () => void
	onCreated: (insp: Inspection) => void
}

const AddInspectionModal: FC<AddInspectionModalProps> = ({
	carrierId,
	open,
	onClose,
	onCreated
}) => {
	const { t } = useTranslation()
	const [form] = Form.useForm()
	const [saving, setSaving] = useState(false)

	const handleOk = async () => {
		const values = await form.validateFields()
		setSaving(true)
		try {
			const insp = await createInspection({
				carrierId,
				...values,
				inspectedAt: values.inspectedAt
					? values.inspectedAt.toISOString()
					: new Date().toISOString()
			})
			onCreated({ ...insp, photos: [] })
			form.resetFields()
			onClose()
		} catch {
			message.error(t('Error'))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Modal
			title="New Inspection"
			open={open}
			onCancel={onClose}
			onOk={handleOk}
			confirmLoading={saving}
			okText="Create"
			destroyOnClose>
			<Form form={form} layout="vertical">
				<Form.Item
					name="inspectedBy"
					label="Inspected By"
					rules={[{ required: true }]}>
					<Input placeholder="Inspector name" />
				</Form.Item>
				<Form.Item
					name="inspectedAt"
					label="Inspection Date">
					<DatePicker
						showTime
						style={{ width: '100%' }}
					/>
				</Form.Item>
				<Form.Item name="notes" label="Notes">
					<Input.TextArea rows={3} />
				</Form.Item>
			</Form>
		</Modal>
	)
}

// ─── Main component ───────────────────────────────────────────────────────────
export const CarrierOneInspections: FC<{ carrierId: number }> = ({
	carrierId
}) => {
	const { t } = useTranslation()
	const [loading, setLoading] = useState(true)
	const [inspections, setInspections] = useState<Inspection[]>([])
	const [addOpen, setAddOpen] = useState(false)

	const load = useCallback(async () => {
		setLoading(true)
		try {
			const data = await fetchInspections(carrierId)
			setInspections(data)
		} catch {
			message.error(t('Error'))
		} finally {
			setLoading(false)
		}
	}, [carrierId, t])

	useEffect(() => {
		load()
	}, [load])

	const handleCreated = (insp: Inspection) =>
		setInspections((prev) => [insp, ...prev])

	const handleDeleted = async (id: number) => {
		try {
			await deleteInspection(id)
			setInspections((prev) => prev.filter((i) => i.id !== id))
		} catch {
			message.error(t('Error'))
		}
	}

	const handlePhotoUploaded = (
		inspectionId: number,
		photo: InspectionPhoto
	) => {
		setInspections((prev) =>
			prev.map((i) =>
				i.id === inspectionId
					? { ...i, photos: [...i.photos, photo] }
					: i
			)
		)
	}

	const handlePhotoDeleted = (
		inspectionId: number,
		photoId: number
	) => {
		setInspections((prev) =>
			prev.map((i) =>
				i.id === inspectionId
					? {
							...i,
							photos: i.photos.filter(
								(p) => p.id !== photoId
							)
						}
					: i
			)
		)
	}

	if (loading) {
		return (
			<Flex justify="center" style={{ paddingTop: 40 }}>
				<Spin />
			</Flex>
		)
	}

	return (
		<>
			<Flex
				vertical
				gap={16}
				style={{ width: '100%', maxWidth: 800 }}>
				<Flex justify="space-between" align="center">
					<Title level={5} style={{ margin: 0 }}>
						Field Inspections
					</Title>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => setAddOpen(true)}>
						New Inspection
					</Button>
				</Flex>

				{inspections.length === 0 ? (
					<Empty description="No inspections recorded" />
				) : (
					<Collapse
						accordion={false}
						style={{ backgroundColor: 'transparent' }}
						items={inspections.map((insp) => ({
							key: insp.id,
							label: (
								<Flex
									align="center"
									justify="space-between"
									gap={8}>
									<Flex
										align="center"
										gap={10}
										wrap="wrap">
										<Text strong>
											{new Date(
												insp.inspectedAt
											).toLocaleDateString(
												'ru',
												{
													day: 'numeric',
													month: 'short',
													year: 'numeric'
												}
											)}
										</Text>
										<Tag color="blue">
											{insp.inspectedBy}
										</Tag>
										<Text
											type="secondary"
											style={{ fontSize: 12 }}>
											{insp.photos.length} photo
											{insp.photos.length !== 1
												? 's'
												: ''}
										</Text>
									</Flex>
									<Tooltip title="Delete inspection">
										<Popconfirm
											title="Delete this inspection and all its photos?"
											onConfirm={() =>
												handleDeleted(insp.id)
											}
											okText="Delete"
											okButtonProps={{
												danger: true
											}}>
											<Button
												icon={
													<DeleteOutlined />
												}
												size="small"
												type="text"
												danger
												onClick={(e) =>
													e.stopPropagation()
												}
											/>
										</Popconfirm>
									</Tooltip>
								</Flex>
							),
							children: (
								<>
									{insp.notes && (
										<Text
											type="secondary"
											style={{
												display: 'block',
												marginBottom: 8,
												fontStyle: 'italic',
												fontSize: 13
											}}>
											{insp.notes}
										</Text>
									)}
									<InspectionPhotos
										inspection={insp}
										onPhotoUploaded={handlePhotoUploaded}
										onPhotoDeleted={handlePhotoDeleted}
									/>
								</>
							)
						}))}
					/>
				)}
			</Flex>

			<AddInspectionModal
				carrierId={carrierId}
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onCreated={handleCreated}
			/>
		</>
	)
}
